import { formatIndianPrice } from '@/utils/formatting';
import { needsTierRules } from './brandOfferTilePreview.rules';
import {
  DAY_MS,
  OFFER_BANNER_COPY,
  OFFER_TYPE_SPEC,
} from './OfferProductPreview.constants';
import type {
  ComputedOfferPrices,
  OfferBannerData,
  OfferPreviewInput,
  OfferTypeSpec,
} from './OfferProductPreview.types';

function formatOfferCountdown(target: Date, now: Date): string {
  const ms = target.getTime() - now.getTime();
  if (ms <= 0) return '00h : 00m : 00s';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${pad(h)}h : ${pad(m)}m : ${pad(s)}s`;
}

function interpolateOfferCopy(
  template: string,
  prices: ComputedOfferPrices
): string {
  const sellFmt = formatIndianPrice(prices.selling);
  const mrpFmt = formatIndianPrice(prices.mrp);
  const pct = prices.percentOff != null ? String(prices.percentOff) : '';
  const saving = prices.flatSaving != null ? String(prices.flatSaving) : '';
  const valueToken = prices.tierValueRendered ?? saving;
  return template
    .replace(
      /\{(discount_price|calculated_price|dynamic_price_based_on_tier|price)\}/gi,
      `₹${sellFmt}`
    )
    .replace(/\{mrp\}/gi, `₹${mrpFmt}`)
    .replace(/\{percent_off\}/gi, pct)
    .replace(/\{value\}/gi, valueToken);
}

function timerSuffix(end: Date | null, now: Date): string | null {
  if (!end) return null;
  const msLeft = end.getTime() - now.getTime();
  if (msLeft <= 0) return null;
  if (msLeft >= DAY_MS) return null;
  return formatOfferCountdown(end, now) + ' left';
}

function daysLeftCaption(msLeft: number): string {
  const days = Math.ceil(msLeft / DAY_MS);
  return days === 1 ? '1 day left' : `${days} days left`;
}

function offerSuffixCaption(
  input: OfferPreviewInput,
  prices: ComputedOfferPrices
): string {
  const rawText = (input.discountConfig?.offerText ?? '').trim();
  return rawText
    ? interpolateOfferCopy(rawText, prices).trim()
    : prices.percentOff != null
      ? `${prices.percentOff}% off`
      : OFFER_BANNER_COPY.offerFallback;
}

function saleBanner(
  input: OfferPreviewInput,
  spec: OfferTypeSpec,
  end: Date | null,
  now: Date
): OfferBannerData {
  if (!end) return { kind: 'NONE' };
  const msLeft = end.getTime() - now.getTime();
  if (msLeft <= 0) return { kind: 'NONE' };

  const saleName = (input.saleName ?? '').trim();

  /* Under 24h: always show live countdown on the tile (not gated by showTimer). */
  if (msLeft < DAY_MS) {
    const countdown = formatOfferCountdown(end, now) + ' left';
    return saleName
      ? { kind: 'CAPTION_SUFFIX', caption: saleName, suffix: countdown }
      : { kind: 'TEXT_ONLY', caption: countdown };
  }

  if (!spec.productPreview.showDaysLeft) return { kind: 'NONE' };

  const dayText = daysLeftCaption(msLeft);
  if (spec.productPreview.showSaleName && saleName) {
    return { kind: 'CAPTION_SUFFIX', caption: saleName, suffix: dayText };
  }
  return { kind: 'TEXT_ONLY', caption: dayText };
}

function couponSuffixBanner(
  input: OfferPreviewInput,
  prices: ComputedOfferPrices,
  end: Date | null,
  now: Date
): OfferBannerData {
  if (needsTierRules(input.offerType) && !prices.showStrike) {
    const rules = input.discountConfig?.rules;
    const hasPositiveTierValue =
      rules?.some((r) => Number(r.value) > 0) ?? false;
    if (!rules?.length || !hasPositiveTierValue) {
      return {
        kind: 'TEXT_ONLY',
        caption: OFFER_BANNER_COPY.addTiersCaption,
      };
    }
    /* Tiers configured but preview cart (sample product price) is outside min/max bands. */
    return { kind: 'NONE' };
  }

  const countdown = input.showTimer ? timerSuffix(end, now) : null;

  if (input.offerType === 'SALE_EXTRA_OFFERS') {
    const caption = offerSuffixCaption(input, prices);
    if (input.showTimer && end) {
      const msLeft = end.getTime() - now.getTime();
      if (msLeft > 0) {
        const countdown =
          msLeft >= DAY_MS ? daysLeftCaption(msLeft) : timerSuffix(end, now);
        if (countdown) {
          return {
            kind: 'PRICE_TIMER',
            prefix: OFFER_BANNER_COPY.getAtPrefix,
            price: prices.selling,
            countdown,
          };
        }
      }
    }
    return {
      kind: 'PRICE',
      prefix: OFFER_BANNER_COPY.getAtPrefix,
      price: prices.selling,
      caption,
    };
  }

  const caption = offerSuffixCaption(input, prices);

  if (countdown) {
    return {
      kind: 'PRICE_TIMER',
      prefix: OFFER_BANNER_COPY.getAtPrefix,
      price: prices.selling,
      countdown,
    };
  }

  return {
    kind: 'PRICE',
    prefix: OFFER_BANNER_COPY.getAtPrefix,
    price: prices.selling,
    caption,
  };
}

export function buildOfferBannerData(
  input: OfferPreviewInput,
  prices: ComputedOfferPrices,
  now: Date
): OfferBannerData {
  if (!input.isActive) return { kind: 'NONE' };

  const spec = OFFER_TYPE_SPEC[input.offerType];
  const end = input.endsAt ? new Date(input.endsAt) : null;
  const expired = end ? end <= now : false;

  if (expired && !input.isSale && !input.showTimer) {
    return { kind: 'NONE' };
  }

  if (input.offerType === 'SALE') {
    return saleBanner(input, spec, end, now);
  }

  if (spec.productPreview.displayText === 'ADMIN_TEXT') {
    const rawText = (input.discountConfig?.offerText ?? '').trim();
    const interpolated = rawText
      ? interpolateOfferCopy(rawText, prices).trim()
      : '';

    const countdown = input.showTimer ? timerSuffix(end, now) : null;
    if (countdown) {
      return interpolated
        ? { kind: 'CAPTION_SUFFIX', caption: interpolated, suffix: countdown }
        : { kind: 'TEXT_ONLY', caption: countdown };
    }
    return interpolated
      ? { kind: 'TEXT_ONLY', caption: interpolated }
      : { kind: 'NONE' };
  }

  if (
    spec.productPreview.pricePrefix === 'GET_AT_PRICE' &&
    spec.productPreview.priceSuffix == null
  ) {
    if (!prices.showStrike) return { kind: 'NONE' };
    const countdown = input.showTimer ? timerSuffix(end, now) : null;
    const caption =
      prices.percentOff != null
        ? `${prices.percentOff}% off`
        : OFFER_BANNER_COPY.offerFallback;
    if (countdown) {
      return {
        kind: 'PRICE_TIMER',
        prefix: OFFER_BANNER_COPY.getAtPrefix,
        price: prices.selling,
        inlineSuffix: caption,
        countdown,
      };
    }
    return {
      kind: 'PRICE',
      prefix: OFFER_BANNER_COPY.getAtPrefix,
      price: prices.selling,
      caption,
    };
  }

  if (spec.productPreview.priceSuffix === 'ADMIN_TEXT') {
    return couponSuffixBanner(input, prices, end, now);
  }

  return { kind: 'NONE' };
}
